import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {DELETE_FILED, FirebaseService} from '../../../../shared-utilities/services-old/firebase.service';
import {filters2Filter, objReKey} from '../../../../shared-utilities/functions-old/object-functions';
import {ColDef} from 'ag-grid-community';
import {IModalDismissResult, ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconColourEnum} from '../../../shared-icons/models/icons-colour.enum';
import {ISharedModalBasicResponse} from '../../../../shared/shared-models/modals/shared-modal-basic';

@Component({
  selector: 'app-save-user-settings',
  templateUrl: './save-user-settings.page.html',
  styleUrls: ['./save-user-settings.page.scss'],
  standalone: false
})
export class SaveUserSettingsPage implements OnInit {

  readonly icons = IconsEnum;

  @Input() getSaveObj?: () => never;
  @Input() currentRoute: string;
  @Input() desc?: string;
  @Input() what: string;
  @Input() confirmLoad = true;
  @Input() entryPrefix: string;
  @Input() entrySuffix: string;
  @Input() colDefs?: ColDef[];
  @Input() order?: string[];
  @Input() localStorageKey: string;
  @Input() isFitToGrid?: boolean;

  isChangeOccurring: boolean;
// eslint-disable-next-line
  savedEntries: {[save: string]: any};
  savedEntriesOrder: string[];

  saveForm: UntypedFormControl;

  selectedSavedEntry: string;

  constructor(
    private firebaseService: FirebaseService,
    private modalController: ModalControllerService,
    private readonly sharedModalUtils: SharedModalUtils
  ) {
  }

  get getNumberOfSavedDocuments(): number {
    return this.savedEntriesOrder.length;
  }

  ngOnInit(): void {
    this.getSavedEntries();
    this.saveForm = new UntypedFormControl(null, [Validators.required, Validators.pattern(/^([\w-]+)+$/)]);
    if (this.entryPrefix && !this.entryPrefix.endsWith('-')) {
      this.entryPrefix += '-';
    }
    if (this.entrySuffix && !this.entrySuffix.startsWith('-')) {
      this.entrySuffix = '-' + this.entrySuffix;
    }
  }

  async onSaveEntry(): Promise<void> {
    const userEntryText: string = this.saveForm.value as string;
    let saveEntry: string = userEntryText;
    if (saveEntry) {
      // Overrides an existing entry
      if (this.savedEntries.hasOwnProperty(saveEntry)) {
        let alertElement = await this.sharedModalUtils.createModal({
          modalTitle: `Overwrite '${userEntryText}'`,
          modalTitleIcon: IconsEnum.infoSlabCircleOutline,
          modalTitleIconColor: IconColourEnum.yellow,
          contextDescription: [`Are you sure you want to save over '${userEntryText}'?`],
          buttonAcceptText: 'Save',
          buttonCloseText: 'Cancel',
          buttonAccept: true,
          buttonClose: true,
          buttonCloseCross: false,
          cssClass: ['shared-basic-modal-css-smaller']
        });
        if (alertElement.data?.buttonPressed !== 'button-accept') {
          return;
        }
        alertElement = await this.sharedModalUtils.createModal({
          modalTitle: `Overwriting '${userEntryText}'`,
          modalTitleIcon: IconsEnum.infoSlabCircleOutline,
          modalTitleIconColor: IconColourEnum.yellow,
          contextDescription: [`This can not be undone.`],
          buttonAcceptText: 'Save',
          buttonCloseText: 'Cancel',
          buttonAccept: true,
          buttonClose: true,
          buttonCloseCross: false,
          cssClass: ['shared-basic-modal-css-smaller']
        });
        if (alertElement.data?.buttonPressed !== 'button-accept') {
          return;
        }
      }

      if (this.entryPrefix && !saveEntry.startsWith(this.entryPrefix)) {
        saveEntry = this.entryPrefix + saveEntry;
      }
      if (this.entrySuffix && !saveEntry.endsWith(this.entrySuffix)) {
        saveEntry += this.entrySuffix;
      }

      this.isChangeOccurring = true;
      const updateEntry = {};
      updateEntry[userEntryText] = {
        data: this.getSaveObj(),
        timestamp: new Date(),
        colDefs:
          Object.values(this.colDefs)
            .map((colDef: ColDef) => colDef.field)
            .filter((field: string) => field),
        isFitToGrid: this.isFitToGrid ?? false
      };

      void this.firebaseService.updateUserPreferences(this.currentRoute, updateEntry)
        .then((): void => {
          this.getSavedEntries();
          this.isChangeOccurring = false
          void this.sharedModalUtils.createModal({
            modalTitle: 'Save Complete',
            modalTitleIcon: IconsEnum.infoSlabCircleOutline,
            modalTitleIconColor: IconColourEnum.green,
            contextDescription: [`Settings saved as '${userEntryText}'`],
            buttonAcceptText: 'OK',
            buttonCloseText: '',
            buttonAccept: true,
            buttonClose: false,
            buttonCloseCross: false,
            cssClass: ['shared-basic-modal-css-smaller']
          }).then((): void => {
            localStorage.setItem(this.localStorageKey, JSON.stringify(this.getSaveObj()));
          });
        });
    }
  }

  async onLoadEntry(): Promise<void> {
    if (this.confirmLoad) {
      const alertElement = await this.sharedModalUtils.createModal({
        modalTitle: 'Load Save',
        modalTitleIcon: IconsEnum.infoSlabCircleOutline,
        modalTitleIconColor: IconColourEnum.yellow,
        contextDescription: [`Load saved data '${this.selectedSavedEntry}'?`],
        buttonAcceptText: 'Load',
        buttonCloseText: 'Cancel',
        buttonAccept: true,
        buttonClose: true,
        buttonCloseCross: false,
        cssClass: ['shared-basic-modal-css-smaller']
      });
      if (alertElement.data?.buttonPressed !== 'button-accept') {
        return;
      }
    }

    const key = this.selectedSavedEntry;
    const entry = this.savedEntries?.[key];

    if (!entry) return;

    const colDefs: string[] = Array.isArray(entry.colDefs) && entry.colDefs.length
      ? entry.colDefs
      : Object.keys(entry.data ?? {});


    const updatedEntry = {
      ...entry,
      timestamp: new Date()
    };

    const payload = {
      [key]: updatedEntry
    };

    void this.firebaseService
      .updateUserPreferences(this.currentRoute, payload)
      .then(() => {
        this.savedEntries[key] = updatedEntry;
      }).catch(err => {
        console.error('Failed to update timestamp', err);
      });

    await this.modalController.dismiss({
      colDefs,
      data: entry.data,
      timestamp: entry.timestamp,
      isFitToGrid: entry.isFitToGrid
    });
  }

// eslint-disable-next-line
  getDisplayDate(savedEntry: any): string {
    if (!savedEntry?.timestamp) {
      return '(No Date Found)';
    }
    const date = savedEntry.timestamp;
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} ` +
      `${date.getHours()}:${(date.getMinutes() < 10 ? '0' : '') + date.getMinutes()}`;
  }

  onSelectChange(savedEntry: string): void {
    if (this.selectedSavedEntry !== savedEntry) {
      this.selectedSavedEntry = savedEntry;
      if (this.saveForm) {
        this.saveForm.setValue(savedEntry);
      }
    } else {
      this.selectedSavedEntry = null;
      if (this.saveForm && this.saveForm.value === savedEntry) {
        this.saveForm.setValue(null);
      }
    }
  }

  onSelectAndLoadChange(savedEntry: string): void {
    this.onSelectChange(savedEntry);
    void this.onLoadEntry().then();
  }

  onInputChange(): void {
    const formSavedEntryValue = this.saveForm.value;
    if (formSavedEntryValue) {
      if (this.selectedSavedEntry && formSavedEntryValue !== this.selectedSavedEntry) {
        this.selectedSavedEntry = null;
      }
      // eslint-disable-next-line
      if (this.savedEntries.hasOwnProperty(formSavedEntryValue)) {
        this.selectedSavedEntry = formSavedEntryValue;
      }
    }
  }

  onClose(): void {
    void this.modalController.dismiss().then();
  }

  onDelete(userEntry: string): void {
    let savedEntry: string = userEntry;
    if (this.entryPrefix && !savedEntry.startsWith(this.entryPrefix)) {
      savedEntry = this.entryPrefix + savedEntry;
    }
    if (this.entrySuffix && !savedEntry.endsWith(this.entrySuffix)) {
      savedEntry += this.entrySuffix;
    }

    void this.sharedModalUtils.createModal({
      modalTitle: 'Confirm Deletion',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextDescription: [`Are you sure you want to delete save '${userEntry}'?`],
      buttonAcceptText: 'Delete',
      buttonCloseText: 'Cancel',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-smaller']
    }).then((response: IModalDismissResult<ISharedModalBasicResponse>): void => {
      if (response.data?.buttonPressed === 'button-accept') {
        this.isChangeOccurring = true;
        const deletedField = {};
        deletedField[userEntry] = DELETE_FILED();
        void this.firebaseService.updateUserPreferences(this.currentRoute, deletedField)
          .then((): void => {
            this.isChangeOccurring = false;
            this.savedEntriesOrder.splice(this.savedEntriesOrder.indexOf(userEntry), 1);
            delete this.savedEntries[userEntry];
          });
      }
    });
  }

  private getSavedEntries(): void {
    this.firebaseService
      .getUserPreferences(this.currentRoute)
      .then((userSavedDocs): void => {
        this.savedEntries = {};
        this.savedEntriesOrder = [];
        if (userSavedDocs) {
          this.assignTimestampToDocuments(userSavedDocs);
          this.savedEntries = userSavedDocs;
          // this.filterPrefixesAndSuffixes(true);
          this.savedEntriesOrder = Object.keys(userSavedDocs)
            .sort((a: string, b: string): number => userSavedDocs[a].timestamp < userSavedDocs[b].timestamp ? -1 : 1);
        }
      })
      .catch((error): void => {
        console.error(`An error has occurred while loading user settings/preferences, please contact support.`, error);
        this.savedEntries = {};
        this.savedEntriesOrder = [];
      });
  }

// eslint-disable-next-line
  assignTimestampToDocuments(userSavedDocs: any): void {
    Object.keys(userSavedDocs).forEach(savedDocName => {
      const ts = userSavedDocs[savedDocName]?.timestamp;

      if (!ts) {
        console.error(`No timestamp found for document user-preference => (${savedDocName})`);
        return;
      }

      if (typeof ts === 'object' && 'toDate' in ts) {
        userSavedDocs[savedDocName].timestamp = ts.toDate();
      }

    });
  }

  private filterPrefixesAndSuffixes(remove: boolean): void {
    // A list of filters to apply to the saves according to the prefixes and suffixes
    const filters: ((save: string) => boolean)[] = [];
    // A list of trim functions to apply to the saves to remove the prefixes and suffixes
    const trimFunctions: ((save: string) => string)[] = [];

    if (this.entryPrefix) {
      filters.push((save: string) => save.startsWith(this.entryPrefix));

      if (remove) {
        trimFunctions.push((save: string) => save.substring(this.entryPrefix.length));
      }
    }

    if (this.entrySuffix) {
      filters.push((save: string) => save.endsWith(this.entrySuffix));

      if (remove) {
        trimFunctions.push((save: string) => save.substring(0, save.length - this.entrySuffix.length));
      }
    }
    const allSaves = Object.keys(this.savedEntries);

    if (!filters.length) {
      return;
    }

    const savesFiltered = allSaves.filter(filters2Filter(filters));
    allSaves.forEach((save) => {
      if (!savesFiltered.includes(save)) {
        delete this.savedEntries[save];
      }
    });

    if (trimFunctions.length > 0) {
      trimFunctions.forEach((f) => {
        objReKey(this.savedEntries, f);
      });
    }
    this.savedEntriesOrder = Object.keys(this.savedEntries);
  }

}
